import { Axios } from '@config/axios';
import { apiEndpoints } from '@constants/apiEndpoints';
import { getCookie } from './localStorage';
import {
  McpAuthorization,
  McpSettings,
  McpSettingsRequest,
  McpScope,
} from '@models/Mcp';

export const MCP_MANAGE_PERMISSION = 'mcp.manage';
const MCP_UNAVAILABLE_STATUS_CODES = [404, 501, 503];
const MCP_REQUEST_CONFIG = {
  validateStatus: (status: number) => status < 500,
};

class McpHttpError<T> extends Error {
  response: { status: number; data: T };

  constructor(status: number, data: T) {
    super(`MCP request failed with HTTP ${status}`);
    this.name = 'McpHttpError';
    this.response = { status, data };
  }
}

export const MCP_SCOPES: Record<McpScope, { label: string; description: string; risk: 'baixo' | 'medio' | 'alto' }> = {
  'mcp.read.projects': {
    label: 'Projetos',
    description: 'Permite consultar obras, unidades, empreendimentos e progresso operacional.',
    risk: 'baixo',
  },
  'mcp.read.budgets': {
    label: 'Orçamentos',
    description: 'Permite consultar orçamentos, composições e dados de planejamento de custos.',
    risk: 'medio',
  },
  'mcp.read.purchase_orders': {
    label: 'Pedidos e ordens',
    description: 'Permite consultar pedidos, ordens de compra, fornecedores e documentos relacionados.',
    risk: 'medio',
  },
  'mcp.read.payroll': {
    label: 'Folha de pagamento',
    description: 'Permite consultar folhas, colaboradores, rateios e dados pessoais sensíveis.',
    risk: 'alto',
  },
  'mcp.read.financials': {
    label: 'Financeiro',
    description: 'Permite consultar pagamentos, saldos, transferências e informações financeiras sensíveis.',
    risk: 'alto',
  },
  'mcp.admin': {
    label: 'Administração MCP',
    description: 'Permite administrar conexões, autorizações e ferramentas MCP do tenant.',
    risk: 'alto',
  },
};

export const ALL_MCP_SCOPES = Object.keys(MCP_SCOPES) as McpScope[];

export function canManageMcp(user: { roles?: string[]; permissions?: string[] }) {
  const roles = user.roles || [];
  const permissions = user.permissions || [];
  return roles.some((role) => ['master', 'admin'].includes(role)) || permissions.includes(MCP_MANAGE_PERMISSION);
}

export function getScopeRisk(scopes: McpScope[]) {
  if (scopes.some((scope) => MCP_SCOPES[scope]?.risk === 'alto')) return 'alto';
  if (scopes.some((scope) => MCP_SCOPES[scope]?.risk === 'medio')) return 'medio';
  return 'baixo';
}

export function getSensitiveScopeWarning(scopes: McpScope[]) {
  const sensitiveScopes = scopes.filter((scope) => MCP_SCOPES[scope]?.risk === 'alto');
  if (sensitiveScopes.length === 0) return '';
  return 'Esta autorização inclui acesso a dados sensíveis como folha de pagamento, financeiro, fornecedores, documentos ou dados pessoais.';
}

export function isMcpBackendUnavailable(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' && MCP_UNAVAILABLE_STATUS_CODES.includes(status);
}

export function getMcpErrorStatus(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status;
}

function throwMcpHttpError<T>(res: { status: number; data: T }) {
  if (res.status >= 400) {
    throw new McpHttpError(res.status, res.data);
  }
  return res.data;
}

function getMcpRequestConfig() {
  const token = getCookie('token');
  return {
    ...MCP_REQUEST_CONFIG,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
}

function getApiOrigin() {
  const apiUrl =
    process.env.NODE_ENV !== 'production' && process.env.REACT_APP_API
      ? process.env.REACT_APP_API
      : 'https://api.elevargestao.com.br';

  return new URL(apiUrl, window.location.origin).origin;
}

export function getMcpServerUrl() {
  return `${getApiOrigin()}/mcp`;
}

export async function getMcpSettings() {
  const res = await Axios.get<McpSettings>(apiEndpoints.MCP.SETTINGS, getMcpRequestConfig());
  return throwMcpHttpError(res);
}

export async function saveMcpSettings(data: McpSettingsRequest) {
  const res = await Axios.put<McpSettings>(apiEndpoints.MCP.SETTINGS, data, getMcpRequestConfig());
  return throwMcpHttpError(res);
}

export async function getMcpAuthorizations() {
  const res = await Axios.get<McpAuthorization[]>(apiEndpoints.MCP.AUTHORIZATIONS, getMcpRequestConfig());
  return throwMcpHttpError(res);
}

export async function deleteMcpAuthorization(id: string) {
  const res = await Axios.delete(`${apiEndpoints.MCP.AUTHORIZATIONS}/${encodeURIComponent(id)}`, getMcpRequestConfig());
  throwMcpHttpError(res);
}
